import { useEffect, useRef } from 'react';
import {
  AmbientLight,
  BoxGeometry,
  BufferGeometry,
  DirectionalLight,
  IcosahedronGeometry,
  Material,
  Mesh,
  MeshStandardMaterial,
  OctahedronGeometry,
  PerspectiveCamera,
  PointLight,
  Scene,
  TorusGeometry,
  TorusKnotGeometry,
  WebGLRenderer,
} from 'three';

type Floater = {
  mesh: Mesh;
  rotSpeed: { x: number; y: number; z: number };
  floatOffset: number;
  floatSpeed: number;
  initialY: number;
};

export default function ThreeBackground({ mood = 1 }: { mood?: number }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const moodRef = useRef(mood);
  moodRef.current = mood;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const width = () => window.innerWidth;
    const height = () => window.innerHeight;

    const scene = new Scene();
    const camera = new PerspectiveCamera(60, width() / height(), 0.1, 100);
    camera.position.z = 9;

    const renderer = new WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' });
    renderer.setSize(width(), height());
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    scene.add(new AmbientLight(0xffffff, 0.6));
    const dir = new DirectionalLight(0xffffff, 1.1);
    dir.position.set(5, 8, 5);
    scene.add(dir);
    const pink = new PointLight(0xfb7185, 1.2, 30);
    pink.position.set(-6, 3, 4);
    scene.add(pink);
    const orange = new PointLight(0xfb923c, 1.2, 30);
    orange.position.set(6, -3, 4);
    scene.add(orange);

    const geometries: BufferGeometry[] = [
      new BoxGeometry(0.9, 0.9, 0.9),
      new IcosahedronGeometry(0.7),
      new TorusGeometry(0.55, 0.22, 12, 24),
      new OctahedronGeometry(0.7),
      new TorusKnotGeometry(0.45, 0.16, 64, 12),
    ];
    const colors = [0xfb923c, 0xf472b6, 0x60a5fa, 0x34d399, 0xfbbf24, 0xa78bfa];

    const floaters: Floater[] = [];
    const materials: Material[] = [];
    const COUNT = 28;
    for (let i = 0; i < COUNT; i++) {
      const geom = geometries[i % geometries.length];
      const mat = new MeshStandardMaterial({
        color: colors[i % colors.length],
        metalness: 0.45,
        roughness: 0.35,
        transparent: true,
        opacity: 0.88,
      });
      materials.push(mat);
      const mesh = new Mesh(geom, mat);
      mesh.position.set(
        (Math.random() - 0.5) * 18,
        (Math.random() - 0.5) * 11,
        (Math.random() - 0.5) * 10 - 3,
      );
      const scale = 0.55 + Math.random() * 0.9;
      mesh.scale.setScalar(scale);
      mesh.userData.baseScale = scale;
      mesh.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI,
      );
      scene.add(mesh);
      floaters.push({
        mesh,
        rotSpeed: {
          x: (Math.random() - 0.5) * 0.025,
          y: (Math.random() - 0.5) * 0.025,
          z: (Math.random() - 0.5) * 0.018,
        },
        floatOffset: Math.random() * Math.PI * 2,
        floatSpeed: 0.5 + Math.random() * 0.8,
        initialY: mesh.position.y,
      });
    }

    let frameId = 0;
    let time = 0;
    // mood 追従を滑らかに (step遷移で急にガクッと変わらないように)
    let smoothMood = moodRef.current;
    const animate = () => {
      time += 0.016;
      smoothMood += (moodRef.current - smoothMood) * 0.05;
      const m = smoothMood;
      const speedFactor = m * m;        // 2x mood → 4x speed
      const floatFactor = 0.5 + m * 0.7;
      const shakeAmp = Math.max(0, m - 1.5) * 0.4;   // 高moodでカメラ振動

      floaters.forEach((f, i) => {
        f.mesh.rotation.x += f.rotSpeed.x * speedFactor;
        f.mesh.rotation.y += f.rotSpeed.y * speedFactor;
        f.mesh.rotation.z += f.rotSpeed.z * speedFactor;
        f.mesh.position.y =
          f.initialY + Math.sin(time * f.floatSpeed + f.floatOffset) * floatFactor;
        // 高moodで scale パルス
        if (m > 1.5) {
          const pulse = 1 + Math.sin(time * 6 + i) * 0.12 * (m - 1);
          f.mesh.scale.setScalar(f.mesh.userData.baseScale * pulse);
        } else {
          f.mesh.scale.setScalar(f.mesh.userData.baseScale);
        }
      });

      // カメラ: 通常はゆるい横揺れ、高moodで全方位ブン回し
      const orbit = time * 0.1 * speedFactor * 0.3;
      camera.position.x = Math.sin(orbit) * (0.5 + m * 0.6) + (Math.random() - 0.5) * shakeAmp;
      camera.position.y = Math.cos(orbit * 0.7) * m * 0.4 + (Math.random() - 0.5) * shakeAmp;
      camera.position.z = 9 - Math.sin(orbit * 0.5) * (m - 1) * 0.8;
      camera.rotation.z = Math.sin(time * 0.4 * speedFactor * 0.2) * (m - 1) * 0.15;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      camera.aspect = width() / height();
      camera.updateProjectionMatrix();
      renderer.setSize(width(), height());
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
      geometries.forEach((g) => g.dispose());
      materials.forEach((m) => m.dispose());
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 1, opacity: 0.6 }}
      aria-hidden
    />
  );
}
